import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { measurementId, startAnalytics, stopAnalytics, trackPageView } from '../core/analytics/google-analytics';
import { useCookieConsent } from '../core/hooks/use-cookie-consent';

/**
 * Reports page views, once the visitor has agreed to be counted.
 *
 * **Two effects, not one, and the order between them is the point.** The first reacts to the
 * decision: granted loads the script, withdrawn switches reporting off and clears what it stored.
 * The second reacts to the location. Keeping them apart is what makes accepting halfway through a
 * visit report the page the visitor is actually on — the location effect re-runs when consent
 * changes, so the first view is sent as soon as there is somewhere to send it, rather than being
 * lost because the navigation happened before permission did.
 *
 * **The title is read after the effect that sets it.** `usePageMetadata` writes `document.title` in
 * an effect of its own, and effects run child-first — this hook is called from `LanguageLayout`,
 * above every page, so by the time it runs the page below has already set its title.
 *
 * **A view is a path, not a history entry.** The booking wizard keeps its step and its selections in
 * the query string and replaces them as the customer works, so keying this on the location's own
 * identity would report a page view for every day clicked in the calendar. Measuring where the
 * wizard is abandoned is worth doing and is an event, not a page.
 */
export function useSiteAnalytics(): void {
    const consent = useCookieConsent();
    const location = useLocation();
    const id = measurementId();

    useEffect(() => {
        if (id === null) {
            return;
        }

        if (consent === 'granted') {
            startAnalytics(id);
        } else if (consent === 'denied') {
            // Only on a refusal, never on "undecided". Nothing has been loaded while undecided, so
            // running the teardown then would delete cookies this site did not set.
            stopAnalytics();
        }
    }, [consent, id]);

    useEffect(() => {
        if (id === null || consent !== 'granted') {
            return;
        }

        trackPageView(window.location.href, document.title);
    }, [consent, id, location.pathname]);
}
